import {Injectable} from '@angular/core';
import {HttpClient, HttpHeaders} from '@angular/common/http';
import {lastValueFrom, Observable, Subject} from 'rxjs';
import {PdfDto} from "../model/pdf-dto";
import {PdfSize} from "../model/position";
import {SignerModel} from "../model/signer-model";
import {SubItemModel} from "../model/subItem.models";

export interface SelfSignUrlData {
    signingType: 'BASIC_SIGN' | 'BASIC_CERTSIGN' | 'HIGH_SIGN' | 'MAX_SIGN' | string;
    url: string;
    process: string;   // UUID
    subItem: string;   // UUID
}

@Injectable({
    providedIn: 'root',
})
export class SipoService {
    private baseUrl = '/api/sipo'; // Backend Base URL

    reloadData: Subject<any> = new Subject();

    constructor(private http: HttpClient) {
    }

    confirmQESSignProcessItem(processId: string, subItem: string) {
        return this.http.put(this.baseUrl + "/qes/sign/" + processId + "/" + subItem, null, {responseType: 'text'});
    }

    /**
     * Upload a PDF file to the server.
     * @param file PDF file to upload
     * @returns Observable of the server response
     */
    uploadPdf(file: File): Observable<any> {
        const formData = new FormData();
        formData.append('file', file);
        return this.http.post<any>(`${this.baseUrl}/upload`, formData);
    }

    uploadPdfPromise(file: File): Promise<any> {
        return lastValueFrom(this.uploadPdf(file));
    }


    postSigningPositions(pdfId: string, data: any, pdfSize: PdfSize): Observable<any> {

        const payload = {
            signingdata: data,
            pdfsize: pdfSize
        }

        return this.http.post(this.baseUrl + "/positions/" + pdfId, payload, {responseType: 'text'});
    }

    getSigningItem(processId: string, subItem: string): Observable<any> {
        return this.http.get(`${this.baseUrl}/process/public/${processId}/subitem/${subItem}`);
    }

    async getSigningItemPromise(processId: string, subItem: string): Promise<any> {
        return await lastValueFrom(this.getSigningItem(processId, subItem));
    }


    getSigningProcessList(): Observable<any> {
        return this.http.get(this.baseUrl + "/process");
    }


    getProcessTypeById(processId: string) {
        return this.http.get(this.baseUrl + "/process/public/" + processId + "/type", {responseType: 'text'});
    }

    // ********************
    deleteProcess(processId: string) {
        return this.http.delete(this.baseUrl + "/process/" + processId);
    }

    // ********************

    getSubItemsList(siginingProcessId: string): Observable<any> {
        return this.http.get(this.baseUrl + "/process/" + siginingProcessId + "/subitems");
    }

    postSignerSubitemByProcess(processId: string): Observable<SubItemModel> {
        return this.http.get<SubItemModel>(this.baseUrl + "/process/" + processId + "/signers/subitem", {responseType: 'json'});
    }

    postSignerModel(data: SignerModel): Observable<any> {
        return this.http.post(this.baseUrl + "/process/" + data.signingProcessId + "/data", data, {responseType: 'json'});
    }

    // ********************************
    updateSignerModel(data: SignerModel): Observable<any> {
        return this.http.put(this.baseUrl + "/process/" + data.signingProcessId + "/data", data, {responseType: 'text'});
    }

    // ********************************

    getSignerModel(signingProcessId: string): Observable<SignerModel> {
        return this.http.get<SignerModel>(this.baseUrl + "/process/public/signings/" + signingProcessId, {responseType: 'json'});
    }

    getSignerProcess(signingProcessId: string): Observable<SignerModel> {
        return this.http.get<SignerModel>(this.baseUrl + "/process/" + signingProcessId, {responseType: 'json'});
    }

    postSignBasicBlob(processId: string, subitem: string, type: string) {
        return this.http.post(this.baseUrl + "/process/types/basic/" + processId + "/" + subitem, null, {responseType: 'blob'});
    }


    postSignBasicByType(processId: string, subitem: string, type: string) {
        return this.http.post(this.baseUrl + `/process/public/signs/${type}/${processId}/${subitem}`, null, {responseType: 'text'});
    }


    /**
     * Get all images from the server.
     * @returns Observable of an array of images
     */
    getImages(pdfId: string): Observable<PdfDto> {
        return this.http.get<PdfDto>(`${this.baseUrl}/images/${pdfId}`);
    }

    getImagesPromise(pdfId: string) {
        return lastValueFrom(this.getImages(pdfId));
    }

    getImagesByProcess(processId: string): Observable<PdfDto> {
        console.log('Request URL:', `${this.baseUrl}/images/process/${processId}`);
        return this.http.get<PdfDto>(`${this.baseUrl}/images/process/${processId}`);
    }

    getImagesByProcessPromise(processId: string) {
        return lastValueFrom(this.getImagesByProcess(processId));
    }


    getPositionsByItem(itemId: string): Observable<any[]> {
        console.log('Request URL:', `${this.baseUrl}/positions/subitems/${itemId}`);
        return this.http.get<any[]>(`${this.baseUrl}/process/subitems/positions/${itemId}`);
    }

    // async getPositionsByItemPromise(itemId: string): Promise<Observable<any[]>> {
    //     console.log('Request URL:', `${this.baseUrl}/positions/subitems/${itemId}`);
    //     return await lastValueFrom(this.getPositionsByItem(itemId));
    // }


    getImageAsBytes(pdfId: string): Observable<any> {
        return this.http.get<any>(`${this.baseUrl}/images/${pdfId}/download`);
    }

    getImageAsBytesPromise(pdfId: string) {
        return lastValueFrom(this.getImageAsBytes(pdfId));
    }


    getFile(fileId: string, fileType: string): Observable<string> {
        return this.http.get(`/api/sipo/providers/documents/${fileId}/${fileType}`, {
            responseType: 'text' // 👈 da Base64 als Text zurückkommt
        });
    }

    getSubitemFile(subItemId: string, fileType: string): Observable<string> {
        return this.http.get(`/api/sipo/process/documents/${subItemId}/${fileType}`, {
            responseType: 'text' // 👈 da Base64 als Text zurückkommt
        });
    }
}
